import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listSpots } from "@/lib/camping.functions";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useEffect, useState } from "react";
import { MapPin, Plus, Camera, MessageCircle, Share2, Check, Search, PlugZap, Waves, Dog, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { campingFaq, campingSections } from "@/data/hub-content";
import { imgSrcSet, CARD_SIZES } from "@/lib/img";

export const Route = createFileRoute("/campingtips/")({
  codeSplitGroupings: [["loader", "component"]],
  // SSR: listan renderas server-side så crawlers ser varje plats-länk direkt.
  loader: async () => {
    const res = await listSpots({ data: {} }).catch(() => ({ spots: [] as any[] }));
    return { spots: res?.spots ?? [], faqLd: faqSchema(campingFaq) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "Campingtips & ställplatser – AK Husbil & Fritid" },
      { name: "description", content: "Riktiga tips på campingar & ställplatser i Sverige – delade av husbilsägare. Läs, kommentera och dela dina favoriter." },
      { property: "og:title", content: "Campingtips & ställplatser" },
      { property: "og:description", content: "Tips från husbilsägare — riktiga platser, kartor och kommentarer." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.akhusbilfritid.se/campingtips" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/campingtips" }],
    scripts: loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : [],
  }),
  component: SpotsIndex,
});

function SpotsIndex() {
  const initial = Route.useLoaderData();
  const fn = useServerFn(listSpots);
  const { data, isLoading } = useQuery({
    queryKey: ["spots"],
    queryFn: () => fn({ data: {} }),
    initialData: initial as { spots: any[] },
  });
  const { signedIn } = useAuth();
  const [q, setQ] = useState("");
  const [region, setRegion] = useState<string>("alla");
  const [spotType, setSpotType] = useState<string>("alla");
  const [needs, setNeeds] = useState<string[]>([]);

  const allSpots = data?.spots ?? [];
  const regions = Array.from(
    new Set(allSpots.map((s: any) => (s.region || "").trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b, "sv"));
  const spotTypes = Array.from(
    new Set(allSpots.map((s: any) => (s.spot_type || "").trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b, "sv"));

  const needle = q.trim().toLowerCase();
  const spots = allSpots.filter((s: any) => {
    if (region !== "alla" && (s.region || "").trim() !== region) return false;
    if (spotType !== "alla" && (s.spot_type || "").trim() !== spotType) return false;
    if (needs.includes("el") && s.has_electricity !== true) return false;
    if (needs.includes("tomning") && s.has_grey_water !== true && s.has_latrine !== true) return false;
    if (needs.includes("hund") && !s.pets_info) return false;
    if (needs.includes("stor") && !s.max_vehicle_length_m && !s.access_info) return false;
    if (!needle) return true;
    return [s.title, s.description, s.region, s.spot_type, s.price_info, s.season_info, s.booking_info, s.surface, s.nearest_services, s.best_for]
      .filter(Boolean)
      .some((v: string) => v.toLowerCase().includes(needle));
  });
  const toggleNeed = (value: string) => setNeeds((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);


  return (
    <section className="bg-warm">
      <div className="container-edge py-16 md:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="eyebrow">Community · Campingtips</p>
            <h1 className="mt-3 font-display text-5xl leading-[1.02] md:text-7xl">
              Riktiga <span className="font-italic-display text-accent">favoriter</span>.
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Campingar och ställplatser tipsade av husbilsägare — inte hittepå-recensioner.
              Läs, kommentera, och dela dina egna favoritplatser.
            </p>
          </div>
          <Link to="/campingtips/ny" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <Plus size={16} /> Dela ditt guldkorn
          </Link>
        </div>

        {/* Invitational CTA — visible for everyone, warmer for signed-out visitors */}
        <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-card p-6 md:p-8 elev-shadow">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">
                {signedIn ? "Din tur" : "Bidra till kartan"}
              </p>
              <h2 className="mt-2 font-display text-2xl leading-tight md:text-3xl">
                {signedIn
                  ? "Har du en favoritplats andra borde upptäcka?"
                  : "Skapa ett konto — dela din bästa plats på 2 minuter."}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">
                Bild, beskrivning, karta — så hjälper du andra husbilsägare hitta dit. Vi granskar inget bort; äkta tips vinner alltid.
              </p>
              <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                <li className="inline-flex items-center gap-1.5"><Camera size={13} /> Ladda upp egna bilder</li>
                <li className="inline-flex items-center gap-1.5"><MapPin size={13} /> Karta & koordinater</li>
                <li className="inline-flex items-center gap-1.5"><MessageCircle size={13} /> Kommentarer från andra</li>
              </ul>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row md:flex-col md:items-end">
              {signedIn ? (
                <Link to="/campingtips/ny" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
                  <Plus size={16} /> Skapa nytt tips
                </Link>
              ) : (
                <>
                  <Link to="/login" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
                    Registrera dig gratis
                  </Link>
                  <Link to="/login" className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-3.5 text-sm font-medium hover:bg-secondary">
                    Har konto? Logga in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {!isLoading && allSpots.length > 0 && (
          <div className="mt-10 flex flex-col gap-4">
            <div className="relative max-w-md">
              <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Sök plats, ort eller typ…"
                aria-label="Sök campingtips"
                className="w-full rounded-full border border-input bg-background py-3 pl-11 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {["alla", ...regions].map((r) => (
                <Button
                  key={r}
                  type="button"
                  onClick={() => setRegion(r)}
                  aria-pressed={region === r}
                  variant="outline"
                  size="sm"
                  className={`rounded-full ${
                    region === r
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-card text-foreground/80 hover:border-accent hover:text-accent"
                  }`}
                >
                  {r === "alla" ? "Alla områden" : r}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {[["alla", "Alla platstyper"], ...spotTypes.map((type) => [type, type])].map(([value, label]) => (
                <Button key={value} type="button" variant="outline" size="sm" onClick={() => setSpotType(value)} aria-pressed={spotType === value}
                  className={`rounded-full ${spotType === value ? "border-accent bg-accent text-accent-foreground hover:bg-accent/90 hover:text-accent-foreground" : ""}`}>
                  {label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" aria-label="Filtrera på behov">
              {[["el", "El", PlugZap], ["tomning", "Tömning", Waves], ["hund", "Hund", Dog], ["stor", "Stora ekipage", Truck]].map(([value, label, Icon]) => {
                const FilterIcon = Icon as typeof PlugZap;
                return (
                  <Button key={value as string} type="button" variant="outline" size="sm" onClick={() => toggleNeed(value as string)} aria-pressed={needs.includes(value as string)}
                    className={`rounded-full ${needs.includes(value as string) ? "border-accent bg-accent text-accent-foreground hover:bg-accent/90 hover:text-accent-foreground" : ""}`}>
                    <FilterIcon size={13} /> {label as string}
                  </Button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              {spots.length} av {allSpots.length} tips
            </p>
          </div>
        )}

        <div className="mt-8">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Laddar…</p>
          ) : allSpots.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-10 text-center">
              <p className="font-display text-2xl">Inga tips ännu.</p>
              <p className="mt-2 text-sm text-muted-foreground">Bli den första att dela en camping eller ställplats!</p>
              <Link to="/campingtips/ny" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground">
                <Plus size={14} /> Lägg upp tips
              </Link>
            </div>
          ) : spots.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-10 text-center">
              <p className="font-display text-2xl">Inga träffar.</p>
              <p className="mt-2 text-sm text-muted-foreground">Prova ett annat sökord eller välj ett annat område.</p>
              <Button
                type="button"
                onClick={() => { setQ(""); setRegion("alla"); setSpotType("alla"); setNeeds([]); }}
                className="mt-6 rounded-full"
              >
                Rensa filter
              </Button>
            </div>
          ) : (
            <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {spots.map((s: any) => <SpotCard key={s.id} spot={s} />)}
            </ul>
          )}
        </div>

        <HubEditorial
          eyebrow="Guide · Ställplatser & camping"
          sections={campingSections}
          faq={campingFaq}
          faqTitle="Vanliga frågor om ställplatser"
        />

      </div>
    </section>
  );
}

function SpotCard({ spot }: { spot: any }) {
  const [img, setImg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [shared, setShared] = useState(false);

  const share = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `https://www.akhusbilfritid.se/campingtips/${spot.slug}`;
    try {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      if (typeof nav.share === "function") {
        await nav.share({ title: spot.title, url });
        return;
      }
      await nav.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    } catch {
      /* avbruten delning */
    }
  };
  useEffect(() => {
    const first = spot.image_urls?.[0];
    if (!first) return;
    if (first.startsWith("http")) { setImg(first); return; }
    supabase.storage.from("user-content").createSignedUrl(first, 60 * 60 * 24)
      .then(({ data }) => setImg(data?.signedUrl ?? null));
  }, [spot]);

  return (
    <li>
      <Link to="/campingtips/$slug" params={{ slug: spot.slug }} className="group block overflow-hidden rounded-3xl border border-border bg-card elev-shadow transition hover:border-accent/60">
        <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
          {img && !failed ? (
            <img
              src={img}
              srcSet={imgSrcSet(img)}
              sizes={CARD_SIZES}
              alt={spot.title}
              loading="lazy"
              decoding="async"
              onError={() => setFailed(true)}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-secondary text-muted-foreground">
              <MapPin size={28} />
              <span className="px-6 text-center text-xs uppercase tracking-wider">
                {spot.region || "Sverige"}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={share}
            aria-label="Dela tipset"
            className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-background/85 px-3 py-1.5 text-[11px] font-medium backdrop-blur transition hover:border-accent hover:text-accent"
          >
            {shared ? <Check size={12} /> : <Share2 size={12} />}
            {shared ? "Kopierad" : "Dela"}
          </button>
        </div>
        <div className="p-5">
          <p className="text-xs uppercase tracking-wider text-accent">{spot.region || "Sverige"} · {spot.spot_type || "camping"}</p>
          <h3 className="mt-2 font-display text-xl leading-tight">{spot.title}</h3>
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{spot.description}</p>
          {(spot.price_info || spot.season_info || spot.has_electricity === true || spot.has_grey_water === true || spot.has_latrine === true) && (
            <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
              {spot.price_info && <span className="rounded-full bg-secondary px-2.5 py-1">{spot.price_info}</span>}
              {spot.season_info && <span className="rounded-full bg-secondary px-2.5 py-1">{spot.season_info}</span>}
              {spot.has_electricity === true && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1"><PlugZap size={11} /> El</span>}
              {(spot.has_grey_water === true || spot.has_latrine === true) && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1"><Waves size={11} /> Tömning</span>}
            </div>
          )}
          {spot.editorial && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              ★ AK-utvalt
            </p>
          )}
        </div>
      </Link>
    </li>
  );
}
